import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Layers, Check, Loader2, RefreshCw } from 'lucide-react';
import { HitApi } from '../../../Api/ApiHit';
import { searchGroup, searchUnit, updateUnit } from '../../../constant/Constant';
import { getSelectedUnit } from '../../../storage/Storage';
import toast from 'react-hot-toast';

function ServiceGroupConfig() {
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            await Promise.all([fetchGroups(), fetchUnitConfig()]);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchGroups = async () => {
        try {
            const json = {
                page: 1,
                limit: 100000,
                unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
            };
            const res = await HitApi(json, searchGroup);
            if (res?.data?.docs) {
                setGroups(res.data.docs);
            } else {
                setGroups([]);
            }
        } catch (err) {
            console.error(err);
            setGroups([]);
        }
    };

    const fetchUnitConfig = async () => {
        try {
            const unitId = getSelectedUnit()?._id || getSelectedUnit()?.[0];
            const res = await HitApi({
                page: 1,
                limit: 1,
                search: { _id: unitId }
            }, searchUnit);

            const unit = res?.data?.docs?.[0];
            if (unit?.appServiceGroupId) {
                const groupId = typeof unit.appServiceGroupId === 'object'
                    ? unit.appServiceGroupId._id
                    : unit.appServiceGroupId;
                setSelectedGroupId(groupId);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleSave = async () => {
        if (!selectedGroupId) {
            toast.error('Please select a service group');
            return;
        }

        setSaving(true);
        try {
            const unitId = getSelectedUnit()?._id || getSelectedUnit()?.[0];
            const res = await HitApi({
                _id: unitId,
                appServiceGroupId: selectedGroupId
            }, updateUnit);

            if (res?.success || res?.statusCode === 200) {
                toast.success('App service group updated successfully');
            } else {
                toast.error(res?.message || 'Failed to save');
            }
        } catch (err) {
            toast.error('Error saving configuration');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="flex justify-center items-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate('/app-control')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                    <ArrowLeft size={20} className="text-gray-600" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">App Service Group</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Select the service group that the mobile app uses to display services
                    </p>
                </div>
            </div>

            {/* Current Selection Info */}
            {selectedGroupId && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2">
                        <Check size={18} className="text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">
                            Currently selected: {groups.find(g => g._id === selectedGroupId)?.name || 'Unknown Group'}
                        </span>
                    </div>
                </div>
            )}

            {/* Group Selection Grid */}
            <div className="bg-white rounded-lg border shadow-sm">
                <div className="p-4 border-b flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Layers size={18} className="text-gray-600" />
                        <h2 className="font-semibold text-gray-900">Select Service Group</h2>
                    </div>
                    <button
                        onClick={loadData}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw size={16} className="text-gray-500" />
                    </button>
                </div>

                {groups.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <Layers size={40} className="mx-auto mb-3 text-gray-300" />
                        <p className="font-medium">No service groups found</p>
                        <p className="text-sm mt-1">Create service groups in Master Services first</p>
                    </div>
                ) : (
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {groups.map((group) => {
                            const isSelected = selectedGroupId === group._id;
                            return (
                                <div
                                    key={group._id}
                                    onClick={() => setSelectedGroupId(group._id)}
                                    className={`relative flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                        isSelected
                                            ? 'border-black bg-gray-50 shadow-sm'
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {/* Selection indicator */}
                                    <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                        isSelected ? 'border-black bg-black' : 'border-gray-300'
                                    }`}>
                                        {isSelected && <Check size={12} className="text-white" />}
                                    </div>

                                    {/* Group image or placeholder */}
                                    {group.img ? (
                                        <img
                                            src={group.img}
                                            alt={group.name}
                                            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                        />
                                    ) : (
                                        <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                                            <Layers size={18} className="text-gray-400" />
                                        </div>
                                    )}

                                    {/* Group info */}
                                    <div className="min-w-0 flex-1">
                                        <p className={`font-medium truncate ${isSelected ? 'text-black' : 'text-gray-800'}`}>
                                            {group.name}
                                        </p>
                                        {group.description && (
                                            <p className="text-xs text-gray-500 truncate mt-0.5">
                                                {group.description}
                                            </p>
                                        )}
                                    </div>

                                    {/* Active badge */}
                                    {group.isActive === false && (
                                        <span className="absolute top-2 right-2 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
                                            Inactive
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Save Button */}
            <div className="flex justify-end mt-6">
                <button
                    onClick={handleSave}
                    disabled={saving || !selectedGroupId}
                    className="flex items-center gap-2 px-6 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {saving ? (
                        <>
                            <Loader2 size={16} className="animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <Check size={16} />
                            Save Configuration
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}

export default ServiceGroupConfig;
